import { useEffect, useState } from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import IntakeWizard from "./IntakeWizard";

const GUIDED_FLOW = [
  { step: "intake", section: "form1", label: "Client Identifying Information" },
  { step: "careplan", section: "goals", label: "Goals & Backup Plan" },
  { step: "profile", section: "assessment", label: "Intake Assessment" },
  { step: "medications", section: "medications", label: "Medication List" },
  { step: "gty", section: "gty", label: "Getting to Know You", optional: true },
];

export function GuidedIntakeModal({ clientId = null, clientName, startIdx = 0, initialForms = {}, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const [cid, setCid] = useState(clientId);
  const [gtyQuestions, setGtyQuestions] = useState(null);
  const [finished, setFinished] = useState(false);
  const [planBuilt, setPlanBuilt] = useState(false);
  const [sigEmail, setSigEmail] = useState("");
  const [sigBusy, setSigBusy] = useState(false);
  const [sigSent, setSigSent] = useState("");
  const creating = !cid;

  const flow = GUIDED_FLOW[idx];

  const loadGtyQuestions = async () => {
    if (gtyQuestions) return;
    try {
      const { data } = await api.get("/questions?active=true");
      setGtyQuestions(data || []);
    } catch { /* wizard falls back to the standard set */ }
  };

  useEffect(() => {
    if (flow.section === "gty") loadGtyQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/clients/${cid}/intake-upload?kind=med_list`, fd);
  };

  const save = async (d) => {
    if (creating && flow.step === "intake" && !cid) {
      if (!(d.full_name || "").trim()) throw new Error("Client full name is required");
      const { data: client } = await api.post("/clients", { intake: d });
      setCid(client.id);
      toast.success(`Client created — ${client.forms?.intake?.full_name || "record ready"}`);
      return;
    }
    await api.put(`/clients/${cid}/forms/${flow.step}`, d);
  };

  const done = () => {
    const nextI = idx + 1;
    if (nextI < GUIDED_FLOW.length) {
      toast.success(`${flow.label} saved — on to the next one`);
      setIdx(nextI);
    } else {
      api.post(`/clients/${cid}/careplan/basic`).then((r) => {
        if (!r.data?.skipped) setPlanBuilt(true);
      }).catch(() => {});
      setFinished(true);
    }
  };

  const close = () => onClose(cid);

  const ensureLink = async () => {
    const { data } = await api.post(`/clients/${cid}/share-links`);
    return data.token;
  };

  const signNow = async () => {
    setSigBusy(true);
    try {
      const t = await ensureLink();
      window.open(`${window.location.origin}/share/${t}`, "_blank");
      toast.success("Signing page opened — hand over the device");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSigBusy(false); }
  };

  const emailLink = async () => {
    if (!/.+@.+\..+/.test(sigEmail)) { toast.error("Enter a valid email address"); return; }
    setSigBusy(true);
    try {
      const t = await ensureLink();
      await api.post(`/clients/${cid}/share-links/${t}/email`, { email: sigEmail });
      setSigSent(sigEmail);
      toast.success(`Signing link emailed to ${sigEmail}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSigBusy(false); }
  };

  if (finished) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-900/50 p-4 backdrop-blur-sm" data-testid="guided-intake-modal">
        <div className="mx-auto mt-16 max-w-xl">
          <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm" data-testid="guided-finish-card">
            <CheckCircle size={40} weight="fill" className="mb-3 text-[#2D6A4F]" />
            <h2 className="font-heading text-2xl font-semibold text-stone-900">All questions answered</h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">
              Everything is filed in the right documents.{planBuilt && (
                <span className="mt-1 block font-medium text-[#2D6A4F]" data-testid="guided-plan-note">
                  A basic, compliant care plan was drafted from the intake answers — the AI deluxe version can replace it anytime.
                </span>
              )}{" "}
              Last step — signatures: the service agreement, all required acknowledgements, and the care plan approval.
            </p>
            <div className="mt-6 space-y-3">
              <Button onClick={signNow} disabled={sigBusy} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="guided-sign-now">
                Sign now on this device
              </Button>
              <div className="flex gap-2">
                <input type="email" value={sigEmail} onChange={(e) => setSigEmail(e.target.value)}
                  placeholder="client-or-family@email.com" data-testid="guided-sign-email-input"
                  className="h-10 flex-1 rounded-md border border-stone-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                <Button onClick={emailLink} disabled={sigBusy} variant="outline" className="border-primary/40 text-primary" data-testid="guided-sign-email-send">
                  Email signing link
                </Button>
              </div>
              {sigSent && <p className="text-xs font-medium text-[#2D6A4F]" data-testid="guided-sign-sent">Sent to {sigSent}</p>}
              <button onClick={close} className="mt-2 w-full text-center text-xs font-medium text-stone-400 hover:text-stone-600 hover:underline" data-testid="guided-finish-done">
                Done — we'll handle signatures later
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-900/50 p-4 backdrop-blur-sm" data-testid="guided-intake-modal">
      <div className="mx-auto mt-8 max-w-2xl pb-16">
        <div className="mb-3 flex items-center justify-between">
          <p className="rounded-md bg-white/90 px-3 py-1.5 text-xs font-medium text-stone-700" data-testid="guided-intake-flow-label">
            Guided intake — {flow.label}{flow.optional ? " (optional)" : ""} · form {idx + 1} of {GUIDED_FLOW.length}
          </p>
          <div className="flex items-center gap-2">
            {flow.optional && (
              <button type="button" onClick={() => done()} className="rounded-md bg-white/90 px-3 py-1.5 text-xs font-medium text-primary hover:bg-white" data-testid="guided-intake-skip-form">
                Skip this form → finish
              </button>
            )}
            <button type="button" onClick={close} className="rounded-md bg-white/90 px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-white" data-testid="guided-intake-close">
              {creating && !cid ? "Cancel" : "Close — continue later"}
            </button>
          </div>
        </div>
        <IntakeWizard
          key={`${cid || "new"}-${idx}`}
          sectionKey={flow.section}
          mode="office"
          clientName={clientName}
          initial={initialForms[flow.step] || {}}
          gtyQuestions={flow.section === "gty" ? (gtyQuestions || []) : undefined}
          onUploadFile={uploadFile}
          askFiller={idx === 0}
          welcome={idx === 0}
          onSave={async (d) => {
            try {
              await save(d);
            } catch (e) {
              toast.error(e?.message || formatApiError(e));
              throw e;
            }
          }}
          onDone={done}
        />
      </div>
    </div>
  );
}
